import { useState } from 'react';
import { useHiddenSectionsContext } from '@/context/HiddenSectionsContext';
import { useUserReportPreferences } from '@/hooks/useUserReportPreferences';
import {
  Modal,
  Button,
  Stack,
  Text,
  Icons,
  SwitchLabel,
  Divider,
} from '@/theme/index';

interface SectionsToHide {
  navSection: string;
  sections: SectionToHide[];
}
export interface SectionToHide {
  nameToDisplay: string;
  nameToRecord: string;
  checked: boolean;
}

interface UserReportPreferencesModalProps {
  sectionsToHide: SectionsToHide[];
  handleOnClose: () => void;
}
export const UserReportPreferencesModal = ({
  sectionsToHide,
  handleOnClose,
}: UserReportPreferencesModalProps) => {
  const { updatePreferences } = useUserReportPreferences();
  const [sections, setSections] = useState<SectionsToHide[]>(sectionsToHide);
  const { reportType } = useHiddenSectionsContext();

  const handleToggle = (navSection: string, sectionIndex: number) => {
    setSections((prevSections: SectionsToHide[]) => {
      const updatedSections = prevSections.map((section) => {
        if (section.navSection === navSection) {
          const updatedInnerSections = section.sections.map(
            (innerSection, index) => {
              if (index === sectionIndex) {
                return {
                  ...innerSection,
                  checked: !innerSection.checked,
                };
              }
              return innerSection;
            }
          );

          return {
            ...section,
            sections: updatedInnerSections,
          };
        }
        return section;
      });

      return updatedSections;
    });
  };

  const handleOnSavePreferences = () => {
    const userReportPreferences = {
      user_report_preferences: {
        [reportType]: sections.flatMap((navSection) =>
          navSection.sections
            .filter((section) => !section.checked)
            .map((section) => section.nameToRecord)
        ),
      },
    };

    updatePreferences.mutate(userReportPreferences);
    handleOnClose();
  };

  return (
    <Modal
      open
      onClose={handleOnClose}
      className="report-preferences-modal"
      sx={{
        '.MuiDialogContent-root': {
          paddingBottom: 0,
        },
      }}
    >
      <Stack direction="column" spacing={2}>
        <Text
          variant="h3"
          className="report-preferences-modal-title"
          display="flex"
          alignItems="center"
        >
          <Icons.Settings sx={{ mr: 1 }} />
          Preferences
        </Text>
        <Text
          variant="body1"
          className="report-preferences-modal-description"
          color="text.secondary"
        >
          Select what section(s) you would like to show from all vehicle
          reports:
        </Text>
        <Stack direction="column" justifyContent="space-between">
          {sections.map((navSection, navIndex) => (
            <Stack direction="column" key={navIndex} spacing={1} mt={2}>
              <Text variant="h3">{navSection.navSection}</Text>
              <Divider />
              {navSection.sections.map((section, sectionIndex) => (
                <Stack
                  direction="column"
                  justifyContent="space-between"
                  key={sectionIndex}
                >
                  <SwitchLabel
                    className="report-preferences-switch"
                    label={section.nameToDisplay}
                    stackStyles={{
                      direction: 'row',
                      alignItems: 'center',
                      spacing: 1,
                      justifyContent: 'space-between',
                      margin: '0',
                      width: '100%',
                    }}
                    setChecked={() =>
                      handleToggle(navSection.navSection, sectionIndex)
                    }
                    checked={section.checked}
                    switchStyles={{
                      marginTop: 0,
                      padding: 0,
                      '.MuiSwitch-track': {
                        '&:after': {
                          content: '""',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          '&:before': {
                            content: '""',
                          },
                        },
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          position="sticky"
          bottom={0}
          pb={2}
          pt={2}
          mt={1}
          sx={{ backgroundColor: 'white' }}
        >
          <Button
            variant="outlined"
            data-testid="close-button"
            onClick={handleOnClose}
            sx={{ borderRadius: 8 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleOnSavePreferences}
            sx={{ borderRadius: 8 }}
          >
            Save Preferences
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
